import { useEffect, useMemo, useState } from "react";
import { SESSION } from "@/lib/mockData";

/**
 * Drives the session countdown timer.
 *
 * Inputs:  sessionStartedAt — epoch millis the session began, or null.
 * Outputs: remainingMs / minutes / seconds derived every second,
 *          plus convenience flags signedOut and showWarning.
 *
 * The hook keeps SessionTimer purely presentational.
 */
export function useSessionCountdown(sessionStartedAt) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  return useMemo(() => {
    if (!sessionStartedAt) {
      return {
        remainingMs: 0,
        minutes: 0,
        seconds: 0,
        signedOut: true,
        showWarning: false,
      };
    }
    const elapsed = now - sessionStartedAt;
    const total = SESSION.idleTimeoutMinutes * 60 * 1000;
    const remaining = Math.max(0, total - elapsed);
    return {
      remainingMs: remaining,
      minutes: Math.floor(remaining / 60000),
      seconds: Math.floor((remaining % 60000) / 1000),
      signedOut: false,
      showWarning:
        elapsed > SESSION.warningAtMinutes * 60 * 1000 && remaining > 0,
    };
  }, [now, sessionStartedAt]);
}
